<template>
  <vs-dialog v-model="isOpen" width="500px" prevent-close @close="close">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-exchange"></i> ໂອນເງິນລະຫວ່າງກະເປົ໋າ
      </h4>
    </template>

    <div class="dialog-content">
      <div class="branch-info">
        <span class="label">ສາຂາ:</span>
        <span class="value">{{ branch.name }} ({{ branch.prefix }})</span>
      </div>

      <div class="form-group">
        <label class="label">ກະເປົ໋າຕົ້ນທາງ <span class="required">*</span></label>
        <div class="select">
          <select v-model="form.source_wallet_type" :disabled="isSubmitting">
            <option value="FREIGHT">ຄ່າຂົນສົ່ງ</option>
            <option value="COD">ເກັບເງິນປາຍທາງ (COD)</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label class="label">ກະເປົ໋າປາຍທາງ <span class="required">*</span></label>
        <div class="select">
          <select v-model="form.dest_wallet_type" :disabled="isSubmitting">
            <option
                v-for="opt in destOptions"
                :key="opt.value"
                :value="opt.value"
            >{{ opt.label }}</option>
          </select>
        </div>
        <p class="help-text help-error" v-if="form.source_wallet_type === form.dest_wallet_type">
          ກະເປົ໋າຕົ້ນທາງ ແລະ ປາຍທາງ ຕ້ອງບໍ່ຊ້ຳກັນ
        </p>
      </div>

      <div class="form-group">
        <label class="label">ຈຳນວນເງິນ (LAK) <span class="required">*</span></label>
        <input
            class="input"
            type="text"
            inputmode="numeric"
            :disabled="isSubmitting"
            :value="formatNumberInput(form.amount)"
            @input="onAmountInput($event.target.value)"
            @blur="$event.target.value = formatNumberInput(form.amount)"
            placeholder="ຈຳນວນເງິນທີ່ຕ້ອງການໂອນ"
        >
        <p class="help-text" v-if="form.amount && !isAmountOverCeiling">
          {{ formatCurrency(form.amount) }}
        </p>
        <p class="help-text help-error" v-if="isAmountOverCeiling">
          ເກີນຈຳນວນສູງສຸດ ({{ formatCurrency(maxTransferAmount) }})
        </p>
      </div>

      <div class="form-group">
        <label class="label">ລາຍລະອຽດ / ເຫດຜົນ <span class="required">*</span></label>
        <textarea
            class="textarea"
            v-model="form.remark"
            rows="3"
            maxlength="500"
            :disabled="isSubmitting"
            placeholder="ເຫດຜົນໃນການໂອນເງິນ (ເຊັ່ນ: ແກ້ໄຂການເຕີມເງິນຜິດກະເປົ໋າ)..."
        ></textarea>
      </div>

      <div class="form-group">
        <label class="label">ສະລິບການໂອນ <span class="required">*</span></label>
        <div class="file-upload">
          <input
              type="file"
              ref="fileInput"
              accept="image/*,.pdf"
              @change="handleFileSelect"
              style="display: none"
          >
          <vs-button
              flat
              @click="$refs.fileInput.click()"
              :disabled="isSubmitting"
          >
            <i class="fal fa-upload"></i> ເລືອກໄຟລ໌
          </vs-button>
          <span v-if="selectedFile" class="file-name">
            {{ selectedFile.name }}
            <i v-if="!isSubmitting" class="fal fa-times" @click="clearFile" style="cursor: pointer; margin-left: 8px;"></i>
          </span>
        </div>
        <p class="help-text">ຮອງຮັບ: JPG, PNG, PDF (ສູງສຸດ 5MB)</p>
      </div>

      <div v-if="previewUrl" class="slip-preview">
        <img v-if="isImageFile" :src="previewUrl" alt="Slip preview">
        <div v-else class="pdf-indicator">
          <i class="fal fa-file-pdf"></i> PDF File
        </div>
      </div>

      <!-- Explicit confirm: restate source -> dest + amount before the money moves. -->
      <div v-if="showConfirm" class="notification is-warning topup-confirm">
        <p><strong>ກວດສອບການໂອນເງິນ</strong></p>
        <p>ສາຂາ: <strong>{{ branch.name }} ({{ branch.prefix }})</strong></p>
        <p>ຈາກ: <strong>{{ walletLabel(form.source_wallet_type) }}</strong>
          → ໄປ: <strong>{{ walletLabel(form.dest_wallet_type) }}</strong></p>
        <p>ຈຳນວນເງິນ: <strong>{{ formatCurrency(form.amount) }}</strong></p>
      </div>

      <div v-if="submitError" class="notification is-warning submit-error">
        <p>{{ submitError }}</p>
        <p v-if="submitErrorRequestId" class="error-ref">ref: {{ submitErrorRequestId }}</p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <template v-if="!showConfirm">
          <vs-button flat @click="close" :disabled="isSubmitting">
            ຍົກເລີກ
          </vs-button>
          <vs-button
              @click="requestConfirm"
              :disabled="isSubmitting || !isFormValid"
          >
            <i class="fal fa-check"></i> ໂອນເງິນ
          </vs-button>
        </template>
        <template v-else>
          <vs-button flat @click="showConfirm = false" :disabled="isSubmitting">
            ກັບຄືນ
          </vs-button>
          <vs-button
              @click="submit"
              :loading="isSubmitting"
              :disabled="isSubmitting"
          >
            <i class="fal fa-check"></i> ຢືນຢັນການໂອນເງິນ
          </vs-button>
        </template>
      </div>
    </template>
  </vs-dialog>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency as _formatCurrency, formatNumberInput } from '@/services/walletFormatters';

// Client-side bounds for a single transfer. Max mirrors Go's MAX_TRANSACTION_AMOUNT (100,000,000 LAK)
// so a fat-finger is rejected before the upload. Min mirrors Go's `amount > 0` (gt=0).
const MIN_TRANSFER_AMOUNT = 1;
const MAX_TRANSFER_AMOUNT = 100000000;

// The two wallet types a branch holds; a transfer moves between them (source != dest).
const WALLET_TYPES = [
  { value: 'FREIGHT', label: 'ຄ່າຂົນສົ່ງ' },
  { value: 'COD', label: 'ເກັບເງິນປາຍທາງ (COD)' },
];

export default {
  name: 'TransferDialog',
  props: {
    branch: {
      type: Object,
      required: true,
    },
    // Source wallet pre-selected from the row that launched the dialog (defaults to FREIGHT,
    // mirroring TopUp). The user can still switch it; dest auto-corrects to the other type.
    sourceWalletType: {
      type: String,
      default: 'FREIGHT',
    },
  },
  data() {
    return {
      isOpen: true,
      isSubmitting: false,
      submitError: null,
      submitErrorRequestId: null,
      // Stable idempotency key for THIS transfer intent. Generated once per dialog instance and
      // reused across resubmits — a human retry after a 60s upload timeout (or an ambiguous error
      // where Go may have committed) must send the SAME key, or Go can't dedup and the branch is
      // moved TWICE for one intent. `transfer-<uuid>` is 45 chars (Go requires 16-64). Rotated
      // (rotateIdempotencyKey) when ANY intent field changes — source/dest type, amount, slip — so
      // a genuinely different transfer isn't deduped against this one (TopUp.vue watches only one
      // wallet_type; a transfer has two selectors, so both must rotate the key).
      idempotencyKey: `transfer-${crypto.randomUUID()}`,
      // Two-step submit: the first click stages a confirm panel; the Go call only fires after
      // explicit confirmation.
      showConfirm: false,
      form: {
        source_wallet_type: this.sourceWalletType === 'COD' ? 'COD' : 'FREIGHT',
        dest_wallet_type: this.sourceWalletType === 'COD' ? 'FREIGHT' : 'COD',
        amount: null,
        remark: '',
      },
      selectedFile: null,
      previewUrl: null,
    };
  },
  computed: {
    // Destination options exclude the currently-selected source type (a transfer cannot be same-type).
    destOptions() {
      return WALLET_TYPES.filter(t => t.value !== this.form.source_wallet_type);
    },
    isFormValid() {
      return (
          this.form.source_wallet_type &&
          this.form.dest_wallet_type &&
          this.form.source_wallet_type !== this.form.dest_wallet_type &&
          this.form.amount >= MIN_TRANSFER_AMOUNT &&
          this.form.amount <= MAX_TRANSFER_AMOUNT &&
          this.form.remark.trim().length > 0 &&
          this.form.remark.length <= 500
      );
    },
    isAmountOverCeiling() {
      return this.form.amount != null && this.form.amount > MAX_TRANSFER_AMOUNT;
    },
    maxTransferAmount() {
      return MAX_TRANSFER_AMOUNT;
    },
    isImageFile() {
      if (!this.selectedFile) return false;
      return this.selectedFile.type.startsWith('image/');
    },
  },
  watch: {
    // A change to any MONEY-INTENT field (amount, source/dest type, slip) rotates the key, so a
    // corrected (genuinely different) transfer is NOT deduped against the prior attempt. Pure
    // resubmits of the same form leave the key untouched → Go dedups → exactly-once move.
    // `remark` is DELIBERATELY NOT a rotation field: it carries no money meaning, and rotating on it
    // would be a money-safety regression — after an ambiguous failure where Go already committed,
    // editing the remark would mint a new key and move money a SECOND time. A stale remark on that
    // rare path is surfaced by Go's is_duplicate → "already processed" toast. (Mirrors TopUp.vue,
    // whose `description` is likewise not rotated.)
    'form.amount'() {
      this.rotateIdempotencyKey();
    },
    'form.source_wallet_type'(newSource) {
      // Keep dest != source: if the user picks a source equal to the current dest, flip dest.
      if (this.form.dest_wallet_type === newSource) {
        this.form.dest_wallet_type = newSource === 'COD' ? 'FREIGHT' : 'COD';
      }
      this.rotateIdempotencyKey();
    },
    'form.dest_wallet_type'() {
      this.rotateIdempotencyKey();
    },
    selectedFile() {
      this.rotateIdempotencyKey();
    },
  },
  methods: {
    rotateIdempotencyKey() {
      // Never rotate while a submit is in flight — a mid-flight intent change must not strip the
      // in-flight request's retry-dedup key (the exact double-move scenario this prevents). The
      // intent inputs are also disabled during submit; this is the correctness backstop.
      if (this.isSubmitting) return;
      this.idempotencyKey = `transfer-${crypto.randomUUID()}`;
      // Any intent change invalidates a staged confirmation.
      this.showConfirm = false;
    },
    walletLabel(type) {
      const found = WALLET_TYPES.find(t => t.value === type);
      return found ? found.label : type;
    },
    close() {
      this.isOpen = false;
      this.$emit('close');
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate MIME type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ປະເພດໄຟລ໌ບໍ່ຖືກຕ້ອງ (ຮອງຮັບ: JPG, PNG, GIF, PDF)');
        event.target.value = '';
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ໄຟລ໌ໃຫຍ່ເກີນໄປ (ສູງສຸດ 5MB)');
        return;
      }

      this.selectedFile = file;

      // Create preview for images
      if (file.type.startsWith('image/')) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = 'pdf';
      }
    },
    clearFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },
    formatNumberInput,
    onAmountInput(value) {
      if (!value) {
        this.form.amount = null;
        return;
      }
      const cleaned = String(value).replace(/[^\d]/g, '');
      this.form.amount = parseInt(cleaned, 10) || null;
    },
    formatCurrency(amount) {
      if (!amount) return '';
      return _formatCurrency(amount);
    },
    // Step 1: validate, then stage the confirm panel. The Go call only fires from submit().
    requestConfirm() {
      if (!this.isFormValid) return;
      // Slip image is required for a transfer (the proof of the real bank move).
      if (!this.selectedFile) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ກະລຸນາອັບໂຫລດສະລິບການໂອນ');
        return;
      }
      this.submitError = null;
      this.submitErrorRequestId = null;
      this.showConfirm = true;
    },
    async submit() {
      // The money call is unreachable without an explicit confirmation stage, regardless of how the
      // component is wired in the future (defense beyond the template's two-step footer).
      if (!this.showConfirm) return;
      if (!this.isFormValid) return;

      // Slip image is required for a transfer.
      if (!this.selectedFile) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ກະລຸນາອັບໂຫລດສະລິບການໂອນ');
        return;
      }

      this.isSubmitting = true;
      this.submitError = null;
      this.submitErrorRequestId = null;

      try {
        const result = await walletService.transferMoney(
          this.$axios,
          this.branch.id,
          this.form.source_wallet_type,
          this.form.dest_wallet_type,
          this.form.amount,
          this.form.remark,
          this.selectedFile,
          this.idempotencyKey // stable per-intent key (reused across resubmits)
        );

        if (result?.success) {
          // is_duplicate lives under result.data (same envelope manual top-up reads). A Go-deduped
          // transfer must show the "already processed" warning, not a fresh-success toast.
          const data = result?.data || result;
          if (data?.is_duplicate) {
            this.$notification.OpenNotification_Error('top-right', 'warning', 5000, 'ລາຍການນີ້ຖືກດຳເນີນການແລ້ວ');
          } else {
            this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
          }
          this.$emit('success', {
            branchId: this.branch.id,
            sourceWalletType: this.form.source_wallet_type,
            destWalletType: this.form.dest_wallet_type,
            sourceBalanceAfter: data?.source_balance_after,
            destBalanceAfter: data?.dest_balance_after,
          });
          this.close();
        } else {
          throw new Error(result?.message || 'ບໍ່ສາມາດໂອນເງິນໄດ້');
        }
      } catch (error) {
        console.error('Transfer error:', error.response?.data || error);
        const { message, requestId } = extractErrorInfo(error);
        const status = error.response?.status;
        const details = error.response?.data?.error?.details || {};
        let msg = message || 'ບໍ່ສາມາດໂອນເງິນໄດ້';
        // A 402 refusal is almost always about money a shipment has already reserved, and this
        // dialog shows no balance at all — so the bare "insufficient balance" reads as a bug on a
        // wallet the list behind it shows as funded. Name the held amount and what is actually
        // spendable. (extractErrorInfo drops `details`; read them from the response like Migrate.)
        if (status === 402 && details.available !== undefined) {
          msg = `ຍອດໃຊ້ໄດ້ ${this.formatCurrency(details.available)} — ຍອດລວມ ${this.formatCurrency(details.balance)}, ຖືກຈອງໄວ້ ${this.formatCurrency(details.reserved)}. ຫຼຸດຈຳນວນ ຫຼື ລໍຖ້າໃຫ້ລາຍການທີ່ຈອງໄວ້ສຳເລັດ.`;
        }
        this.submitError = msg;
        this.submitErrorRequestId = requestId;
        this.isSubmitting = false;
        // Return to the form so the error is visible. The SAME idempotencyKey is retained, so a
        // resubmit of the unchanged form is deduped by Go (exactly-once move).
        this.showConfirm = false;
      }
    },
  },
  beforeDestroy() {
    if (this.previewUrl && this.previewUrl !== 'pdf') {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #007bff;
  }
}

.dialog-content {
  padding: 20px;
}

.branch-info {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;

  .label {
    color: #666;
    margin-right: 8px;
  }

  .value {
    font-weight: 600;
    color: #333;
  }
}

.form-group {
  margin-bottom: 16px;

  .label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
  }

  .required {
    color: #dc3545;
  }

  .input,
  .textarea,
  .select select {
    width: 100%;
  }

  .help-text {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }

  .help-error {
    color: #dc3545;
  }
}

.file-upload {
  display: flex;
  align-items: center;
  gap: 12px;

  .file-name {
    color: #28a745;
    font-size: 14px;
  }
}

.slip-preview {
  margin-top: 16px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 8px;
    border: 1px solid #ddd;
  }

  .pdf-indicator {
    padding: 40px;
    background: #f8f9fa;
    border-radius: 8px;
    color: #dc3545;

    i {
      font-size: 48px;
      display: block;
      margin-bottom: 8px;
    }
  }
}

.submit-error {
  margin-top: 16px;

  .error-ref {
    font-size: 12px;
    color: #856404;
    margin-top: 4px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
